import React from "react";
import { HStack, Button, Box, Pressable } from "native-base";
import {
  PaperPlaneIcon,
  MoreHorizontalIcon,
} from "../../../assets/icons/icon-assets";
import VoiceMessage from "../../../components/common/VoiceMessage";
import { VoiceMessageHandle } from "../../../components/common/VoiceMessage";
import AVInputtext from "../../../components/AVInputtext";
import { CloseIcon, AddIcon } from "native-base";
import { Loc } from "../../../App/Loc";
import { Media } from "../../../AVCore/Media";
import { TrashIcon } from "../../../assets/icons/icon-assets";
import { setMediaList } from "../../../Redux/Actions/userActions";
import Offline from "react-offline";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import type SunEditorCore from "suneditor/src/lib/core";

const ChatInput = ({
  menuRef,
  imageInputRef,
  addPhotoIconRef,
  handleImages,
  setIsOpenBox,
  isOpenBox,
  mdlConvoVisible,
  handleChatMessage,
  value,
  debouncedHandleChatMessage,
  handleMessage,
  isSetText,
  showEmoji,
  inputRef,
  editingImage,
  isLoading,
  Loading,
  isOpenConvo,
  handleSendConvoMessage,
  isGroupChat,
  convo,
  isHuddleChatTab,
  handleUpdatePost,
  itemEdit,
  handleSendMessage,
  toFK,
  media,
  setMedia,
  subject,
  images,
  setImages,
  showConvoFilePicker,
  showFilePicker,
  setIsRecording,
  isRecording,
  handleVoiceRecordingComplete,
  waveformBars,
  recordingTime,
  isMobile,
  setShowEmoji,
  shrink,
  replyValue,
  handleKeyPress,
  setRteValue,
  useRichText,
  setUseRichText,
  entityData,
}) => {
  const hasAudio = media?.some((x) => x?.type?.startsWith("audio"));
  const audioFile = media?.find((x) => x?.type?.startsWith("audio"));

  const removeAudio = (item) => {
    const filterImages = images?.filter((x) => x?.id !== item?.id);
    setImages(filterImages);

    const filterMedia = media?.filter((x) => x?.id !== item?.id);
    setMedia(filterMedia);
  };

  const isSendDisabled =
    // 1. If convo open but disabled → disable
    (isOpenConvo && !convo?.enabled) ||
    // 2. Otherwise check huddle entity & input validity
    (!(
      // convo open & huddle entity not deleted & (text/media/image exists)
      (
        isOpenConvo &&
        entityData?.status !== "Deleted" &&
        // Plain text check
        ((value?.trim()?.length !== undefined && value?.trim()?.length !== 0) ||
          // Media or Images
          media?.length !== 0 ||
          images.length !== 0)
      )
    ) &&
      entityData?.status !== "Deleted" &&
      // Case A: text/media/rte exists but no recipient
      (((value?.trim()?.length !== 0 || media?.length !== 0) &&
        toFK?.length === 0) ||
        // Case B: multiple recipients but no subject
        (toFK?.length > 1 && !subject) ||
        // Case C: nothing entered at all
        ((value?.trim()?.length === undefined || value?.trim()?.length === 0) &&
          media?.length === 0) ||
        // Case D: no recipient selected
        toFK?.length === 0));

  React.useEffect(() => {
    inputRef?.current?.focus();
  }, [shrink, isSetText, replyValue]);

  const voiceRef = React.useRef<VoiceMessageHandle>(null);

  React.useEffect(() => {
    voiceRef.current?.reset?.();
  }, [convo?.id]);

  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        voiceRef.current?.reset?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <HStack
      ref={wrapperRef}
      px={1}
      space={1}
      alignItems="center"
      width="100%"
      zIndex="-3"
    >
      <input
        type="file"
        accept="image/jpeg,image/jfif,image/png,image/gif,video/mp4,"
        multiple
        hidden
        ref={imageInputRef}
        onChange={handleImages}
      />

      {!useRichText && !isRecording && !hasAudio && (
        <Offline>
          {({ isOffline }) => (
            <Button
              ref={menuRef}
              p={2}
              rounded="full"
              _hover={{ opacity: 0.8 }}
              onPress={() => setIsOpenBox(!isOpenBox)}
              backgroundColor="AVColor.primary"
              isDisabled={
                isOffline ||
                entityData?.status ===
                  "Deleted" /* || showFilePicker || showConvoFilePicker*/
              }
            >
              {!useRichText && !isOpenBox ? (
                <AddIcon size="12px" color="white" />
              ) : (
                !useRichText && <CloseIcon size="12px" color="white" />
              )}
            </Button>
          )}
        </Offline>
      )}

      <Box flex={1} position="relative">
        {!useRichText && hasAudio ? ( // For displaying the recorded voice message preview
          <HStack
            bg="gray.100"
            px={3}
            py={2}
            borderRadius="full"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            borderWidth={1}
            borderColor="gray.300"
          >
            {/*Trash Icon */}
            <Pressable onPress={() => removeAudio(audioFile)}>
              <TrashIcon height="20" width="20" />
            </Pressable>

            <HStack alignItems="center" flex={1} mx={3}>
              <VoiceMessage
                audioUrl={Media.getURL(audioFile)}
                onRecordingComplete={() => {}}
                isNewVoiceChat={true}
              />
            </HStack>
          </HStack>
        ) : !useRichText && isRecording ? ( // For recording audio
          <HStack
            bg="gray.100"
            px={3}
            py={2}
            borderRadius="full"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            borderWidth={1}
            borderColor="gray.300"
          >
            <HStack alignItems="center" flex={1} mx={3}>
              <VoiceMessage
                onRecordingComplete={() => {}}
                hasAudio={hasAudio}
                isRecording={isRecording}
                recordingAudioTime={recordingTime}
                isNewVoiceChat={true}
              />
            </HStack>
          </HStack>
        ) : (
          <Offline>
            {({ isOffline }) => (
              <AVInputtext
                type="text"
                minRows="100f"
                size="medium"
                multiline={true}
                maxLength={undefined}
                onChange={
                  mdlConvoVisible && value?.trim()?.length == 0
                    ? handleChatMessage
                    : mdlConvoVisible && value?.trim()?.length > 0
                      ? debouncedHandleChatMessage
                      : handleMessage
                }
                defaultValue={undefined}
                width="100%"
                value={
                  isSetText
                    ? value
                    : showEmoji
                      ? value
                      : value?.trim()?.length == 0
                        ? value
                        : undefined
                }
                height={"25px"}
                label={Loc.currentLang.menu.typeYourMsgHere}
                placeholder={undefined}
                inputRef={inputRef}
                onKeyPress={!isOffline ? handleKeyPress : undefined}
                shrink={shrink}
                isConvo={true}
                showEmoji={showEmoji}
                setShowEmoji={setShowEmoji}
                setRteValue={setRteValue}
                useRichText={useRichText}
                setUseRichText={setUseRichText}
                isMobile={isMobile}
                disabled={entityData?.status === "Deleted"}
                isItemEdit={itemEdit?.length !== 0}
                //hasAudio={hasAudio}
                //isRecording={isRecording}
                //recordingTime={recordingTime}
                //waveformBars={waveformBars}
              />
            )}
          </Offline>
        )}
      </Box>

      <HStack space={1} alignItems="center">
        {!useRichText && !hasAudio && (
          <VoiceMessage
            ref={voiceRef}
            onRecordingComplete={handleVoiceRecordingComplete}
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            isDisabled={!isSendDisabled}
          />
        )}

        <Offline>
          {({ isOffline }) => (
            <Button
              p={2}
              isDisabled={
                isOffline || isSendDisabled || entityData?.status === "Deleted"
              }
              isLoading={
                !useRichText ? (isLoading ? isLoading : Loading) : false
              }
              onPress={
                !isOpenConvo && isGroupChat && !isHuddleChatTab
                  ? handleSendMessage
                  : itemEdit?.length !== 0
                    ? () => handleUpdatePost(itemEdit)
                    : () => handleSendConvoMessage(convo)
              }
              backgroundColor={isLoading || Loading ? "#32AE78" : "white"}
              rounded="full"
              height="32px"
              width="32px"
            >
              <PaperPlaneIcon size="20px" color="#32AE78" />
            </Button>
          )}
        </Offline>
      </HStack>
    </HStack>
  );
};

export default ChatInput;
